/* =========================================================================
 * WHY "CONNECT MY GMAIL" COULD NOT START — IN WORDS, NEVER AS A CODE.
 *
 * Brian clicked "Connect my Gmail" and NOTHING HAPPENED: no Google screen, no
 * error, no visible response. Measured live in his browser, the click did fire
 * and the server did answer, precisely:
 *
 *     POST /api/rep/google/start
 *     409 { ok:false, error:'rep_connect_failed',
 *           detail:{ code:'rep_email_missing',
 *                    detail:'Your roster record has no email address, so a
 *                            Gmail grant could not be proved to be yours.
 *                            Ask Brian to add it, then connect.' } }
 *
 * The sentence a human needs was in the response the whole time. The client
 * put the OBJECT `{code, detail}` into component state and rendered it, React
 * threw #31 ("objects are not valid as a React child"), the subtree unmounted,
 * and the Connect card VANISHED. So the one thing that would have explained
 * the failure was the thing that destroyed the screen.
 *
 * This module exists so that path can never be silent again, and so the logic
 * is testable without a DOM: everything here is pure and returns a STRING.
 *
 * ⚠️ These functions MUST NOT be able to return an object. `verify-connect-
 * error-prose.mjs` asserts exactly that, with the real 409 body above as its
 * fixture. That gate fails against the code as it shipped this morning.
 * ========================================================================= */

import { proseOf } from './api.js'

/* The FLOOR, not the first choice. The server's own prose always wins — it is
 * the source of truth and is already written for a person. This is what a rep
 * reads if some deploy ever answers with a bare code and no sentence. A code
 * must never reach the screen on its own. */
export const WHY_CONNECT_FAILED = {
  rep_email_missing:
    "We couldn't start the connection because your account has no email address on file. "
    + 'Until it is set, a Gmail grant cannot be proved to belong to you.',
  service_may_not_compose:
    'This is a machine login rather than a person, so it cannot connect a mailbox. '
    + 'Sign in as yourself and try again.',
  oauth_not_configured:
    'This deploy has no Google OAuth client configured, so there is nothing to connect to. '
    + 'That is a server setting, not something you can fix from here.',
  identity_mismatch:
    'The Google account you connected is not the address on your roster record, '
    + 'so the grant was not kept.',
}

/* The sentence above says WHAT is wrong. This says WHERE to go. Also prose. */
export const WHERE_TO_FIX = {
  rep_email_missing: 'Team Management → your own roster record → the Email field.',
  identity_mismatch: 'Connect the Google account that matches the Email on your roster record.',
}

/**
 * A sentence, always — never an object, never a bare code, never blank.
 *
 * Order: the server's prose, then the floor above, then a last resort that at
 * least names the HTTP status. The last resort is deliberately useless-but-
 * visible, because a useless message beats a card that disappears.
 *
 * @param {unknown} err  an ApiError from lib/api.js, or anything at all
 * @returns {string}     always a non-empty string
 */
export function failureSentence(err) {
  const e = err && typeof err === 'object' ? err : {}
  const code = proseOf(e.body?.detail?.code) || proseOf(e.body?.code) || proseOf(e.body?.error)
  const server = proseOf(e.body?.detail) || proseOf(e.body?.reason) || proseOf(e.detail)

  let sentence = server || (code && WHY_CONNECT_FAILED[code]) || ''
  if (!sentence) {
    sentence = `We couldn't start the connection (${proseOf(e.status) || 'network'}).`
    // A code shown as a LAST resort is still prose around it, and it is at
    // least searchable — which is what a rep needs to report it.
    if (code) sentence += ` The server called it "${code}" — send that to Brian if it keeps happening.`
  }

  const where = (code && WHERE_TO_FIX[code]) || ''
  if (where && !sentence.includes(where)) sentence += ` Where to fix it: ${where}`
  return sentence
}
